import { useMemo } from 'react';
import { Calendar, DollarSign, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { cn } from '~/lib/utils';

/**
 * Parse an ISO date string (YYYY-MM-DD) to extract year and month
 * This avoids timezone issues that occur when using new Date() on ISO strings
 */
function parseISODate(dateStr: any): { year: number; month: number } {
  if (typeof dateStr === 'string' && dateStr.includes('-')) {
    const [year, month] = dateStr.split('-').map(Number);
    return { year, month };
  }
  // Fallback to Date parsing for Date objects
  const d = new Date(dateStr);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/**
 * Calculate number of monthly payments based on school year dates
 * This mirrors the backend PaymentScheduleCalculator logic
 */
export function calculateMonthlyPaymentCount(startDate: any, endDate: any): number {
  if (!startDate || !endDate) {
    return 10; // Default fallback for typical school year
  }

  const start = parseISODate(startDate);
  const end = parseISODate(endDate);

  // Calculate months difference and add 1 to include both start and end months
  const months = ((end.year - start.year) * 12) + (end.month - start.month) + 1;
  // Clamp to 1-12 like the backend does
  return Math.max(1, Math.min(months, 12));
}

interface PaymentSchedulePreviewProps {
  totalCents: number;
  discountCents?: number;
  scheduleType: string;
  billingDayOfMonth?: number;
  startMonth?: number;
  schoolYearStart?: any;
  schoolYearEnd?: any;
  className?: string;
  showDiscount?: boolean;
  existingInvoices?: any[];
}

/**
 * PaymentSchedulePreview - Displays a preview of payment schedule
 */
export function PaymentSchedulePreview({
  totalCents,
  discountCents = 0,
  scheduleType,
  billingDayOfMonth = 1,
  startMonth,
  schoolYearStart,
  schoolYearEnd,
  className,
  showDiscount = true,
  existingInvoices
}: PaymentSchedulePreviewProps) {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatDate = (date: any) => {
    if (typeof date === 'string') {
      return date;
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Calculate schedule from props
  const schedule = useMemo(() => {
    // If existing invoices are provided, use them
    if (existingInvoices && existingInvoices.length > 0) {
      return existingInvoices.map(inv => ({
        date: formatDate(new Date(inv.dueDate)),
        amount: inv.amountCents,
        isPaid: inv.status === 'PAID'
      }));
    }

    if (!totalCents || !scheduleType) return [];

    const netTotal = Math.max(totalCents - discountCents, 0);

    // Calculate number of payments
    let numPayments: number;
    let monthIncrement: number;
    switch (scheduleType) {
      case 'annual':
        numPayments = 1;
        monthIncrement = 12;
        break;
      case 'biannual':
        numPayments = 2;
        monthIncrement = 6;
        break;
      case 'monthly':
      default:
        // Use canonical calculation if we have school year dates
        numPayments = calculateMonthlyPaymentCount(schoolYearStart, schoolYearEnd);
        monthIncrement = 1;
        break;
    }

    const amountPerPayment = Math.floor(netTotal / numPayments);
    const remainder = netTotal % numPayments;

    // Generate schedule entries
    const entries: any[] = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    // Determine start month - use explicit startMonth, schoolYearStart, or default to September
    const effectiveStartMonth = startMonth ||
      (schoolYearStart ? parseISODate(schoolYearStart).month : 9);

    let currentMonth = effectiveStartMonth - 1; // 0-indexed
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < numPayments; i++) {
      // Handle year wrap
      const adjustedMonth = currentMonth % 12;
      const year = currentMonth >= 12 ? currentYear + 1 : currentYear;
      const monthName = monthNames[adjustedMonth];
      const amount = amountPerPayment + (i < remainder ? 1 : 0);

      entries.push({
        date: `${monthName} ${billingDayOfMonth}, ${year}`,
        amount,
        isPaid: false
      });

      currentMonth += monthIncrement;
    }

    return entries;
  }, [totalCents, discountCents, scheduleType, billingDayOfMonth, startMonth, schoolYearStart, schoolYearEnd, existingInvoices]);

  const netTotal = Math.max(totalCents - discountCents, 0);

  if (schedule.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-4", className)}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Payment Schedule
          </CardTitle>
          {schedule.length > 1 && (
            <CardDescription>
              {schedule.length} payments
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary */}
          {showDiscount && totalCents > 0 && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(totalCents)}</span>
              </div>
              {discountCents > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount:</span>
                  <span>-{formatCurrency(discountCents)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(netTotal)}</span>
              </div>
            </div>
          )}

          {/* Schedule List */}
          <div className="divide-y">
            {schedule.map((payment: any, idx: number) => (
              <div
                key={idx}
                className={cn(
                  "flex justify-between items-center py-3",
                  payment.isPaid && "text-muted-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  {payment.isPaid ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className={cn(
                    "text-sm",
                    payment.isPaid && "line-through"
                  )}>
                    {payment.date}
                  </span>
                </div>
                <span className={cn(
                  "font-medium",
                  payment.isPaid && "text-green-600"
                )}>
                  {formatCurrency(payment.amount)}
                  {payment.isPaid && " (Paid)"}
                </span>
              </div>
            ))}
          </div>

          {/* Per-payment amount note */}
          {schedule.length > 1 && !existingInvoices && (
            <p className="text-xs text-muted-foreground text-center">
              Each payment is approximately {formatCurrency(Math.floor(netTotal / schedule.length))}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PaymentSchedulePreview;
